import {
    Action, ActionType, Bot, CompletedTrick, Difficulty, Engine, EngineEvent, EventType,
    Phase, PlayerIndex, State
} from "@ravalmatic/euchre-engine";
import { GameConstants } from "../../GameConstants";

const { LOCAL_PLAYER, PLAYER_NAMES } = GameConstants;

export type Scheduler = (delay: number, callback: () => void) => () => void;

export interface BoardSnapshot {
    state: State;
    legalActions: Action[];
    busy: boolean;
    displayedTrick: CompletedTrick | null;
    bids: string[];
    error: string | null;
}

export class BoardManager {
    public readonly engine: Engine;
    private readonly bots: Map<PlayerIndex, Bot>;
    private cancelTimer?: () => void;
    private displayedTrick: CompletedTrick | null = null;
    private busy = false;
    private destroyed = false;
    private error: string | null = null;
    private stopped = false;
    private bids = ["", "", "", ""];

    constructor(
        seed: number, difficulty: Difficulty,
        private readonly schedule: Scheduler,
        private readonly render: (snapshot: BoardSnapshot) => void
    ) {
        this.engine = new Engine({ seed });
        this.bots = new Map(([1, 2, 3] as const).map(player => [player, new Bot(player, difficulty)]));
        for (const type of Object.values(EventType)) this.engine.addEventListener(type, this.onEngineEvent, this);
    }

    public start(): void {
        this.submit({ type: ActionType.DEAL });
    }

    public getSnapshot(): BoardSnapshot {
        return {
            state: this.engine.getState(), legalActions: this.engine.getLegalActions(LOCAL_PLAYER),
            busy: this.busy, displayedTrick: structuredClone(this.displayedTrick),
            bids: [...this.bids], error: this.error
        };
    }

    public submit(action: Action): boolean {
        if (this.destroyed || this.busy || this.stopped) return false;
        this.error = null;
        if (action.type !== ActionType.DEAL && action.playerIndex !== LOCAL_PLAYER) {
            this.error = "Only the local player's actions can be submitted by the board.";
            this.publish();
            return false;
        }
        return this.engine.processAction(action);
    }

    public selectCard(cardId: number): void {
        const phase = this.engine.getState().phase;
        this.submit({
            type: phase === Phase.DISCARD ? ActionType.DISCARD : ActionType.PLAY_CARD,
            playerIndex: LOCAL_PLAYER, cardId
        });
    }

    public destroy(): void {
        this.destroyed = true;
        this.cancelTimer?.();
        this.cancelTimer = undefined;
        for (const type of Object.values(EventType)) this.engine.removeEventListener(type, this.onEngineEvent, this);
    }

    private onEngineEvent(event: EngineEvent): void {
        if (this.destroyed) return;
        switch (event.type) {
            case EventType.ACTION_REJECTED:
                console.warn("Euchre action rejected", event.message, event.action);
                this.error = event.message ?? "The engine rejected this action.";
                this.publish();
                return;
            case EventType.DEAL_EXECUTED:
                this.bids = ["", "", "", ""];
                break;
            case EventType.BID_MADE:
                if (event.action.type === ActionType.PASS) {
                    this.bids[event.action.playerIndex] = "Pass";
                } else if (event.action.type === ActionType.ORDER_UP || event.action.type === ActionType.CALL_TRUMP) {
                    this.bids[event.action.playerIndex] = `${event.state.trump}${event.action.alone ? " / alone" : ""}`;
                }
                break;
            case EventType.TRICK_COMPLETED:
                this.displayedTrick = event.trick ?? null;
                break;
            case EventType.HAND_COMPLETED:
            case EventType.TURN_CHANGED:
                this.advance();
                break;
        }
    }

    private advance(): void {
        this.cancelTimer?.();
        if (this.displayedTrick) {
            this.busy = true;
            this.publish();
            this.cancelTimer = this.schedule(1200, () => {
                this.cancelTimer = undefined;
                if (this.destroyed) return;
                this.displayedTrick = null;
                this.busy = false;
                this.publish();
                this.scheduleBot();
            });
        } else {
            this.publish();
            this.scheduleBot();
        }
    }

    private publish(): void {
        if (!this.destroyed) this.render(this.getSnapshot());
    }

    private scheduleBot(): void {
        const state = this.engine.getState();
        if (this.destroyed || this.busy || this.stopped || state.currentPlayerIndex === LOCAL_PLAYER
            || ![Phase.BIDDING, Phase.DISCARD, Phase.PLAYING].includes(state.phase)) return;
        const bot = this.bots.get(state.currentPlayerIndex);
        if (!bot) throw new Error("Missing bot for active player");
        this.cancelTimer = this.schedule(750, () => {
            this.cancelTimer = undefined;
            if (this.destroyed) return;
            try {
                const action = bot.getAction(this.engine.getObservation(bot.playerIndex));
                this.engine.processAction(action);
            } catch (error) {
                console.error(`Euchre bot failed: ${PLAYER_NAMES[bot.playerIndex]}`, error);
                this.stopped = true;
                this.error = `${PLAYER_NAMES[bot.playerIndex]} could not play. Please start a new match.`;
                this.publish();
            }
        });
    }
}
